'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

type RevealState = 'idle' | 'armed' | 'in';

/**
 * Reveal — fades/slides its children in when they scroll into view.
 *
 * The initial SSR render is the resting (visible) state, so content never
 * blanks without JS and reduced-motion users see it immediately. On mount we
 * arm the hidden state and reveal on intersect. `delay` staggers siblings.
 */
export function Reveal({
  children,
  className,
  delay = 0,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<RevealState>('idle');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = ref.current;

    if (!el) return;

    setState('armed');

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setState('in');
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );

    io.observe(el);

    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn(state === 'armed' && 'reveal-armed', state === 'in' && 'reveal-in', className)}
      style={state === 'in' ? { transitionDelay: `${delay}s` } : undefined}
    >
      {children}
    </div>
  );
}
